import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Upload, Download, Trash2, FileText, Camera } from "lucide-react";
import { AttachmentThumb } from "./AttachmentThumb";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";

const MAX = 10 * 1024 * 1024;
const ACCEPT = ".jpg,.jpeg,.png,.webp,.gif,.heic,.pdf,.doc,.docx,.xls,.xlsx,.txt";
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const Attachments = ({ carId, voucherNumber, title, testId = "attachments-section" }) => {
  const { t, fmt } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const camera = useRef(null);
  const base = `/cars/${carId}/vouchers/${voucherNumber}/attachments`;

  const load = useCallback(() => api.get(base).then((r) => setItems(r.data)), [base]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      if (f.size > MAX) { toast.error(`${f.name}: ${t("fileTooLarge")}`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try { await api.post(base, fd); } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setBusy(false);
    if (input.current) input.current.value = "";
    if (camera.current) camera.current.value = "";
    load();
  };

  const open = async (a) => {
    try {
      const { data } = await api.get(`/attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.original_filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (a) => {
    if (!window.confirm(`${t("confirmDeleteFile")}\n${a.original_filename}`)) return;
    try { await api.delete(`/attachments/${a.id}`); toast.success(t("fileDeleted")); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const canDelete = (a) => user.role === "admin" || a.uploaded_by_id === user.id;
  const images = items.filter((a) => a.content_type.startsWith("image/"));
  const files = items.filter((a) => !a.content_type.startsWith("image/"));

  return (
    <div className="no-print border-t pt-4 space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-bold flex items-center gap-2"><Paperclip className="w-4 h-4" />{title || t("attachments")} ({items.length})</h4>
        <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" data-testid="attachment-file-input" onChange={(e) => upload([...e.target.files])} />
        <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" data-testid="attachment-camera-input" onChange={(e) => upload([...e.target.files])} />
        <div className="flex gap-2">
          <Button size="sm" disabled={busy} data-testid="attachment-camera-btn" className="bg-[#F5BE25] hover:bg-[#E5AC12] text-[#3E3E40]" onClick={() => camera.current?.click()}>
            <Camera className="w-4 h-4" />{t("takePhoto")}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} data-testid="attachment-upload-btn" onClick={() => input.current?.click()}>
            <Upload className="w-4 h-4" />{busy ? t("uploading") : t("attachFile")}
          </Button>
        </div>
      </div>
      {items.length === 0 && <p className="text-xs text-slate-500">{t("noAttachments")}</p>}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="attachment-thumbs">
          {images.map((a) => <AttachmentThumb key={a.id} a={a} canDelete={canDelete(a)} onDelete={remove} />)}
        </div>
      )}
      <ul className="space-y-1.5">
        {files.map((a) => (
          <li key={a.id} className="flex items-center gap-2 border rounded-md px-3 py-2 bg-slate-50" data-testid={`attachment-${a.id}`}>
            <FileText className="w-4 h-4 text-slate-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" dir="auto">{a.original_filename}</div>
              <div className="text-[11px] text-slate-500">{kb(a.size)} · {a.uploaded_by} · {fmt(a.created_at)}</div>
            </div>
            <Button size="sm" variant="ghost" data-testid={`attachment-download-${a.id}`} onClick={() => open(a)}><Download className="w-4 h-4" /></Button>
            {canDelete(a) && (
              <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid={`attachment-delete-${a.id}`} onClick={() => remove(a)}><Trash2 className="w-4 h-4" />{t("delete")}</Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
